import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

interface FloatingBulkBarProps {
  count: number;
  onClear: () => void;
  /** Localized label for the count, defaults to "Выбрано" */
  countLabel?: string;
  /** Action buttons rendered to the right of the divider */
  children?: React.ReactNode;
  /** Hide entirely when count === 0 (default true) */
  hideWhenEmpty?: boolean;
  className?: string;
  ariaLabel?: string;
  /** Allow actions to wrap instead of hiding the last buttons on narrow viewports. */
  wrapActions?: boolean;
}

/**
 * Floating bulk-action pill — Gmail/Linear-style.
 * Renders fixed at bottom-center; never pushes layout when selection appears.
 * Generic shell — pass action buttons via children.
 */
export const FloatingBulkBar: React.FC<FloatingBulkBarProps> = ({
  count,
  onClear,
  countLabel = 'Выбрано',
  children,
  hideWhenEmpty = true,
  className,
  ariaLabel = 'Действия с выбранными элементами',
  wrapActions = false,
}) => {
  if (hideWhenEmpty && count === 0) return null;

  return (
    <div
      className={cn(
        'fixed bottom-6 left-1/2 z-40 max-w-[calc(100vw-2rem)] -translate-x-1/2',
        className,
      )}
      role="region"
      aria-label={ariaLabel}
    >
      {/* Анимация живёт на внутреннем элементе: её transform не должен перебивать
          центрирующий -translate-x-1/2 на обёртке (иначе панель «прыгает» из-под правого края).
          Из-за overflow-x-auto любые выпадающие панели внутри обязаны использовать portal. */}
      <div className={cn(
        'flex max-w-full items-center gap-2 rounded-xl border border-gray-800 bg-gray-900 py-2 pl-4 pr-2 text-white shadow-medium animate-slide-up',
        wrapActions ? 'flex-wrap justify-center' : 'overflow-x-auto sm:rounded-full',
      )}>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-sm font-medium whitespace-nowrap">
            {countLabel}: <span className="tabular-nums">{count}</span>
          </span>
          <button
            onClick={onClear}
            className="inline-flex items-center justify-center h-7 w-7 rounded-full text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
            aria-label="Сбросить выбор"
            title="Сбросить"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {children && (
          <>
            <div className="h-6 w-px bg-gray-700 mx-1" />
            <div className={cn('flex items-center gap-1.5', wrapActions ? 'flex-wrap justify-center' : 'shrink-0')}>
              {children}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FloatingBulkBar;
